import { Card, CardContent } from "@/shared/ui/card.tsx"
import { Carousel, CarouselContent, CarouselItem } from "@/shared/ui/carousel.tsx"
// import Autoplay from "embla-carousel-autoplay"
import { motion } from "motion/react"
import { useState } from "react"
import { ChevronRight, type LucideIcon } from "lucide-react"
import { cn, getTrueDate } from "@/lib/utils"
import { DateTime } from "luxon"

type Props = {
  name: string,
  description: string,
  emoji: string, // Changed from icon to emoji
  icon?: LucideIcon, // Added optional right icon
  onNavigate: () => void,
  className?: string
}

const ThemeCampaignCard = ({ name, description, emoji, icon: RightIcon = ChevronRight, onNavigate, className }: Props) => {

    const datesNextMonth: string[] = Array.from({ length: 7 }, (_, i) =>
    DateTime.now()
      .setZone("America/New_York") // Eastern Time
      .plus({ months: 1 ,days: i + 4 })
      .toISODate()! // format: YYYY-MM-DD
  );


  const dates: string[] = Array.from({ length: 7 }, (_, i) =>
    DateTime.now()
      .setZone("America/New_York") // Eastern Time
      .plus({ days: i })
      .toISODate()! // format: YYYY-MM-DD
  );

  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      data-testid="campaign"
      onClick={onNavigate}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(className, "relative cursor-pointer group h-32 flex items-center gap-4 px-5 flex-1 border-2 border-purple-200 hover:border-purple-300 rounded-2xl overflow-hidden bg-white hover:shadow-sm transition-all duration-200")}
    >
      {/* Subtle background pattern */}
      <div
        className={cn(
          "absolute inset-0 z-0 opacity-30",
          "[background-size:20px_20px]",
          "[background-image:linear-gradient(to_right,#f3f4f6_1px,transparent_1px),linear-gradient(to_bottom,#f3f4f6_1px,transparent_1px)]",
        )}
      />

      {/* Radial gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white via-white to-purple-50/30"></div>

      {/* Emoji instead of icon */}
      <div className="flex items-center justify-center size-12 text-2xl z-10 group-hover:scale-110 transition-transform duration-300">
        {emoji}
      </div>

      {/* Content */}
      <div className="flex-1 space-y-0 z-10 min-w-0">
        <motion.h3
          initial={false}
          animate={{
            fontSize: isHovered ? "1.8rem" : "1.4rem",
          }}
          transition={{
            duration: 0.3,
            ease: "easeInOut",
          }}
          className="font-semibold text-purple-500 group-hover:text-purple-700 transition-colors leading-tight"
        >
          {name}
        </motion.h3>
        <motion.p
          className="text-xs text-gray-500 leading-relaxed"
          animate={{
            opacity: isHovered ? 0.7 : 1,
            y: isHovered ? -2 : 0,
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {description}
        </motion.p>
      </div>

      {/* Right icon - always visible */}
      <div className="flex items-center gap-3 justify-center z-10">
        <Carousel
          opts={{
            align: "start",
            loop: true,
            duration: 20, // Reduced duration for smoother transitions
            skipSnaps: false,
            dragFree: false,
          }}
          plugins={[
            // Autoplay({
            //   delay: 1900, // Reduced delay
            //   stopOnInteraction: false,
            //   stopOnMouseEnter: false,
            //   stopOnFocusIn: false,
            // }),
          ]}
          orientation="vertical"
          className="h-24 sm:h-32 rotate-180"
        >
          <CarouselContent className="h-20 sm:h-28 -mt-4">
            {dates.map((card, index) => (
              <CarouselItem key={`${card}-${index}`} className="pt-4 basis-1/2">
                <Card className="size-20 sm:size-28 border border-purple-400" style={{transform: "rotate(180deg)"}}>
                  <CardContent className="flex items-center flex-col justify-center h-full w-full p-3">
                    <p className="font-poppins text-base sm:text-lg font-semibold text-indigo-600">{getTrueDate(card, "MMM")}</p>
                    <p className="font-montserrat text-2xl sm:text-6xl font-semibold text-indigo-600">{getTrueDate(card, "d")}</p>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <Carousel
          opts={{
            align: "center",
            loop: true,
            duration: 20, // Reduced duration for smoother transitions
            skipSnaps: false,
            dragFree: false,
          }}
          plugins={[
            // Autoplay({
            //   delay: 1700, // Reduced delay
            //   stopOnInteraction: false,
            //   stopOnMouseEnter: false,
            //   stopOnFocusIn: false,
            // }),
          ]}
          orientation="vertical"
          className="h-32 hidden md:block"
        >
          <CarouselContent className="h-28 -mt-4">
            {datesNextMonth.map((card, index) => (
              <CarouselItem key={index} className="pt-4 basis-1/2">
                <Card className="size-28 border border-purple-400">
                  <CardContent className="flex items-center flex-col justify-center h-full w-full p-3">
                    <p className="font-poppins text-lg font-semibold text-indigo-600">{getTrueDate(card, "MMM")}</p>
                    <p className="font-montserrat text-6xl font-semibold text-indigo-600">{getTrueDate(card, "d")}</p>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <motion.div
          animate={{
            scale: isHovered ? 1.1 : 1,
            x: isHovered ? 2 : 0,
          }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="flex items-center justify-center size-8 rounded-full bg-purple-100 group-hover:bg-purple-200 transition-colors duration-200"
        >
          <RightIcon className="size-4 text-purple-500" />
        </motion.div>
      </div>
    </div>
  )
}

export default ThemeCampaignCard;
